import {
  Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ToastService } from 'src/app/services/toast.service';
import { ValidationService } from 'src/app/services/validation.service';
import {
  buildValidationSheetCsv,
  validationSheetFilename,
  parseAttendanceSheet,
} from 'src/app/utils/validation-sheet.utils';

const MAX_SHEET_BYTES = 5 * 1024 * 1024;

@Component({
  selector: 'app-course-validation-details',
  templateUrl: './course-validation-details.component.html',
  styleUrls: [ './course-validation-details.component.scss' ],
})
export class CourseValidationDetailsComponent implements OnInit, OnDestroy {
  tableTitles = [
    { name: 'Full Name', sortBy: 'firstName' },
    {
      name: 'Email', sortBy: 'loginName', class: 'email',
    },
    { name: 'Hours', sortBy: 'adjustedValue' },
    {
      name: 'Status', sortBy: 'currentStatusText', class: 'status',
    },
  ]
  sortOrder = { sortBy: 'currentStatusText', direction: 'ASC' };
  statusPriority: Record<string, number> = {
    registered: 1,
    pending: 2,
    'take survey': 3,
    completed: 4,
    missed: 5,
    cancelled: 6,
  };
  isLoading = true;
  sessionValidationDetails: any;
  selectedSession: any
  updatedValidations: {
    user: any;
    statusKid: string;
    adjustedValue: number;
  }[] = [];
  originalValidations: {
    user: any;
    originalStatusKid: string;
    originalAdjustedValue: number;
  }[] = [];
  bulkSubscription: any;
  validationSubscription: Subscription;
  @ViewChild('validationSheetInput') sheetInput?: ElementRef<HTMLInputElement>;

  constructor(
    private validationService: ValidationService,
    private toastService: ToastService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CourseValidationDetailsComponent>,
  ) { }



  ngOnInit(): void {
    this.selectedSession = this.data
    this.validationService.clearSessionValidationDetails();
    this.validationService.getSessionValidationDetails(this.data);

    this.validationSubscription = this.validationService.validationSessionData$
      .subscribe((response: any) => {
        if (!response) return;

        this.sessionValidationDetails = response;
        this?.sessionValidationDetails?.rows.forEach((user: any) => {
          const cloneUser = JSON.parse(JSON.stringify(user));

          this.originalValidations.push({
            user: cloneUser,
            originalStatusKid: cloneUser.currentStatusKid,
            originalAdjustedValue: cloneUser.adjustedValue,
          })

          this?.updateIndividualValidation(user, user.currentStatusKid);
        })
        this.sortColumn('currentStatusText');
      })

  }

  closeDialog = () => {
    this.dialogRef.close();
  }

  sortColumn = (sortBy: string) => {
    if (!this.sessionValidationDetails?.rows) return;

    const newDirection = this.sortOrder.sortBy === sortBy
      ? (this.sortOrder.direction === 'ASC' ? 'DESC' : 'ASC')
      : 'ASC';

    this.sortOrder = { sortBy, direction: newDirection };

    this.sessionValidationDetails.rows.sort((a: any, b: any) => {
      let comparison: number;

      if (sortBy === 'currentStatusText') {
        const priorityA = this.statusPriority[String(a[sortBy] ?? '').toLowerCase()] ?? 99;
        const priorityB = this.statusPriority[String(b[sortBy] ?? '').toLowerCase()] ?? 99;
        comparison = priorityA - priorityB;
      } else if (sortBy === 'firstName') {
        comparison = String(a.firstName ?? '').localeCompare(String(b.firstName ?? ''))
          || String(a.lastName ?? '').localeCompare(String(b.lastName ?? ''));
      } else {
        const valA = a[sortBy];
        const valB = b[sortBy];
        const numA = Number(valA);
        const numB = Number(valB);
        comparison = !isNaN(numA) && !isNaN(numB)
          ? numA - numB
          : String(valA ?? '').localeCompare(String(valB ?? ''));
      }

      return this.sortOrder.direction === 'ASC' ? -comparison : comparison;
    });
  }

  updateIndividualValidation = (user: any, validation: any) => {
    // if the user is already in the updatedValidations array, remove it
    const userIndex = this.updatedValidations.findIndex((updatedValidation: any) => updatedValidation.user.loginName === user.loginName);
    if (userIndex !== -1) {
      this.updatedValidations.splice(userIndex, 1);
    }
    // if the user is unmarked, don't add them to the updatedValidations array
    if (validation === null) {
      return;
    }
    // add the updated user to the updatedValidations array
    const updatedValidation = {
      user: user,
      statusKid: validation,
      adjustedValue: Number(user.adjustedValue),
    }
    // add the updated user to the updatedValidations array
    this.updatedValidations.push(updatedValidation);
  }

  approveAll = () => {
    this.sessionValidationDetails.rows.forEach((user: any) => {
      this.updateIndividualValidation(user, 'COMPLETED');
    })
  }

  unmarkAll = () => {
    this.sessionValidationDetails.rows.forEach((user: any) => {
      this.updateIndividualValidation(user, 'REGISTERED');
    })
  }

  denyAll = () => {
    this.sessionValidationDetails.rows.forEach((user: any) => {
      this.updateIndividualValidation(user, 'NOT_TAKEN');
    })
  }


  // these are functions for styling the buttons
  isApproved = (user: any) => {
    const isApprovedArray = this?.updatedValidations?.filter((validatedUser: any) => validatedUser.user.loginName === user.loginName && validatedUser.statusKid === 'COMPLETED');
    return isApprovedArray.length > 0;
  }

  isUnmarked = (user: any) => {
    const isUnmarkedArray = this?.updatedValidations?.filter((validatedUser: any) => validatedUser.user.loginName === user.loginName);
    return !(isUnmarkedArray.length > 0);
  }

  isDenied = (user: any) => {
    const isDeniedArray = this?.updatedValidations?.filter((validatedUser: any) => validatedUser.user.loginName === user.loginName && validatedUser.statusKid === 'NOT_TAKEN');
    return isDeniedArray.length > 0;
  }

  // this styles the select all buttons
  styleButton = (status: any) => {
    if (status === 'COMPLETED') {
      const markedCompleted = this.updatedValidations.filter((validatedUser: any) => validatedUser.validation === 'COMPLETED');
      const isSelected = markedCompleted.length > 0 && markedCompleted.length === this.sessionValidationDetails?.rows?.length;
      return isSelected ? true : false;
    } else if (status === 'NOT_TAKEN') {
      const markedDenied = this.updatedValidations.filter((validatedUser: any) => validatedUser.validation === 'NOT_TAKEN');
      const isSelected = markedDenied.length > 0 && markedDenied.length === this.sessionValidationDetails?.rows?.length;
      return isSelected ? true : false;
    }
    return
  }

  updateAdjustedValue(user: any, event: Event) {
    const inputElement = event.target as HTMLInputElement;
    let eventValue = Number(inputElement.value);
    if (eventValue < 0) {
      eventValue = 0;
      inputElement.value = '0';
    }
    const updatedValidation = this.updatedValidations.find((updatedValidation: any) => {
      return updatedValidation.user.loginName === user.loginName;
    });
    if (updatedValidation) {
      updatedValidation.adjustedValue = eventValue;
    }
    user.adjustedValue = eventValue;
  }

  getUpdatedUsers() {
    const originalValidations = this.originalValidations;
    const updatedValidations = this.updatedValidations;


    return updatedValidations.filter((updatedValidation) => {
      const originalValidation = originalValidations.find((originalValidation) => originalValidation.user.loginName === updatedValidation.user.loginName);

      if (!originalValidation) {
        throw new Error('Original validation not found');
      }

      const validationSame = updatedValidation.statusKid === originalValidation.originalStatusKid;
      const adjustedValueSame = updatedValidation.adjustedValue == originalValidation.originalAdjustedValue;

      return !(validationSame && adjustedValueSame);
    });
  }

  // this saves the updated validations
  saveValidationUpdates = () => {
    const updatedUsers = this.getUpdatedUsers();

    const formattedUpdatedValidations = updatedUsers.map((updatedValidation) => {
      return {
        id: parseFloat(updatedValidation.user.userCourseId),
        currentStatusKid: updatedValidation.statusKid,
        adjustedValue: updatedValidation.adjustedValue,
      }
    })
    // here we remove any validations that haven't been updated
    const filteredValidations = formattedUpdatedValidations.filter((validation: any) => validation.currentStatusKid !== validation.originalStatusKid);
    // here we are packaging the data to send to the backend
    const validationPackage = {
      courseId: this.selectedSession.courseId,
      courseItemId: this.selectedSession.courseItemId,
      updateArr: formattedUpdatedValidations,
    }
    this.isLoading = true;
    this.bulkSubscription = this.validationService.bulkValidate(validationPackage).subscribe({
      next: (response: any) => {
        this.dialogRef.close('result');
        this.isLoading = false;
        this.toastService.setToast({
          text: 'Validations updated successfully', type: 'success', icon: true,
        })
      },
      error: (error: Error) => {
        this.isLoading = false;
        console.log('error', error);
        throw error;
      },
    })

  }

  // ---- Bulk sign-in sheet (download / upload) ----

  downloadValidationSheet = () => {
    const rows = this.sessionValidationDetails?.rows ?? [];
    if (rows.length === 0) {
      this.toastService.setToast({
        text: 'There are no registered users to download.', type: 'warn', icon: true,
      });
      return;
    }

    const courseName = this.data?.courseName ?? '';
    const courseItemId = this.data?.courseItemId ?? '';

    const csv = buildValidationSheetCsv(
      courseName, courseItemId, rows,
    );
    const filename = validationSheetFilename(courseName, courseItemId);

    const blob = new Blob([ csv ], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  triggerSheetUpload = () => {
    const input = this.sheetInput?.nativeElement;
    if (!input) return;
    // Clear first so re-selecting the same file still fires (change).
    input.value = '';
    input.click();
  }

  onSheetUpload = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    if (file.size > MAX_SHEET_BYTES) {
      this.toastService.setToast({
        text: 'That file is too large. Please upload a sign-in sheet under 5MB.',
        type: 'error',
        icon: true,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.applyAttendanceSheet(String(reader.result ?? ''));
    };
    reader.onerror = () => {
      this.toastService.setToast({
        text: 'Could not read the uploaded file. Please try again.', type: 'error', icon: true,
      });
    };
    reader.readAsText(file);
  }

  applyAttendanceSheet = (text: string) => {
    const {
      attendedEmails, invalidFormat, sheetCourseId,
    } = parseAttendanceSheet(text);

    if (invalidFormat) {
      this.toastService.setToast({
        text: 'That file doesn\'t look like a sign-in sheet. It needs an Email column and an Attended column.',
        type: 'error',
        icon: true,
      });
      return;
    }

    // Guard against uploading a sheet downloaded for a different course. The
    // Course ID column is optional (hand-made sheets omit it); we only reject
    // when the sheet carries an ID and it disagrees with this course.
    const currentCourseId = String(this.data?.courseItemId ?? '').trim();
    if (sheetCourseId && currentCourseId && sheetCourseId !== currentCourseId) {
      this.toastService.setToast({
        text: `This sign-in sheet is for a different course (Course ID ${sheetCourseId}). Please upload the sheet downloaded for this course.`,
        type: 'error',
        icon: true,
      });
      return;
    }

    const usersByEmail = new Map<string, any>();
    (this.sessionValidationDetails?.rows ?? []).forEach((user: any) => {
      const email = String(user?.loginName ?? '').trim().toLowerCase();
      if (email) usersByEmail.set(email, user);
    });

    let markedCount = 0;
    let alreadyCompletedCount = 0;
    let offRosterCount = 0;

    attendedEmails.forEach((email) => {
      const user = usersByEmail.get(email);
      if (!user) {
        offRosterCount += 1;
        return;
      }
      this.updateIndividualValidation(user, 'COMPLETED');
      // Only count users whose status actually changes — this mirrors what
      // Save will persist, so the toast can't overstate the result.
      if (String(user.currentStatusKid) === 'COMPLETED') {
        alreadyCompletedCount += 1;
      } else {
        markedCount += 1;
      }
    });

    if (markedCount === 0) {
      let reason: string;
      if (attendedEmails.size === 0) {
        reason = 'No attendees were marked in the sheet.';
      } else if (alreadyCompletedCount > 0) {
        reason = 'Every matched attendee was already completed.';
      } else {
        reason = 'None of the marked attendees matched a registered user.';
      }
      this.toastService.setToast({
        text: `${reason} Nothing was changed.`, type: 'warn', icon: true,
      });
      return;
    }

    const skipped = offRosterCount > 0
      ? ` ${offRosterCount} off-roster email${offRosterCount === 1 ? '' : 's'} skipped.`
      : '';
    this.toastService.setToast({
      text: `${markedCount} user${markedCount === 1 ? '' : 's'} marked completed.${skipped} Review and press Save to apply.`,
      type: 'success',
      icon: true,
    });
  }

  ngOnDestroy(): void {
    this.validationSubscription.unsubscribe();
    this.bulkSubscription?.unsubscribe();
  }
}
